import type { MilitaryDocumentType } from "@/core/models/global/DocumentUploadStatus";

// Statuses where the applicant is not the service member: no service record of their own, so no JST/LES.
export const SPOUSE_OR_DEPENDENT_STATUSES = ["Military Spouse", "Veteran Spouse", "Military Dependent", "Veteran Dependent"];

// The profile Military Information answers the Military and Veterans documents depend on.
export interface MilitaryAnswers {
    MilitaryStatus?: string;
    CurrentMilitaryStatus?: string;
    UsingGiBill?: string;
    GiBillBenefits?: string;
    TuitionAssistanceExpected?: string;
    MilitaryBranch?: string;
}

// Air Force and Space Force service members earn their college credit through the Community College of the Air Force, not
// the Joint Services Transcript the other branches use. Both are stored in the same "jst" document slot.
const CCAF_BRANCHES = ["Air Force", "Space Force"];

export interface ServiceTranscriptLabel {
    title: string;
    description: string;
}

export function getServiceTranscriptLabel(info: MilitaryAnswers): ServiceTranscriptLabel {
    return CCAF_BRANCHES.includes(info.MilitaryBranch ?? "")
        ? {
            title: "Community College of the Air Force (CCAF)",
            description: "Upload your CCAF transcript to evaluate your military training and experience for college credit.",
        }
        : {
            title: "Joint Services Transcript (JST)",
            description: "Evaluate your military training and experience for college credit.",
        };
}

// The benefit choice that means the applicant will not use GI Bill benefits: no Certificate of Eligibility is asked for
// (decided 2026-10-28; it used to stay required because GI Bill was still Yes).
export const NOT_USING_BENEFITS = "Not using Benefits";

export interface MilitaryDocumentRequirements {
    // The profile step's status ("Please Select Your Status") is answered. Without it we cannot tell a service member from a
    // spouse or dependent, so no service-member document (JST/LES) is asked for.
    statusKnown: boolean;
    isSpouseOrDependent: boolean;
    showGiBillSection: boolean;
    showServiceSection: boolean;
    isUsingTuitionAssistance: boolean;
    required: MilitaryDocumentType[];
}

// Which documents the Military and Veterans page asks for. The dashboard tile (useVeteranBenefitsStatus) should use the same
// rule so the tile and the page agree.
export function getMilitaryDocumentRequirements(info: MilitaryAnswers): MilitaryDocumentRequirements {
    const status = info.CurrentMilitaryStatus ?? "";
    const statusKnown = info.MilitaryStatus === "Yes" && status !== "";
    const isSpouseOrDependent = SPOUSE_OR_DEPENDENT_STATUSES.includes(status);
    const showGiBillSection = statusKnown && info.UsingGiBill === "Yes" && info.GiBillBenefits !== NOT_USING_BENEFITS;
    const showServiceSection = statusKnown && !isSpouseOrDependent;
    const isUsingTuitionAssistance = info.TuitionAssistanceExpected === "Yes";

    const required: MilitaryDocumentType[] = [];
    if (showGiBillSection) required.push("coe");
    if (showServiceSection) required.push("jst");
    if (showServiceSection && isUsingTuitionAssistance) required.push("les");

    return { statusKnown, isSpouseOrDependent, showGiBillSection, showServiceSection, isUsingTuitionAssistance, required };
}
